import { Search } from "@mui/icons-material";
import { Box, Card, IconButton, InputAdornment, TableContainer, TextField, Chip, Menu, MenuItem, ListItemText, Tooltip, useTheme, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import { useEffect, useState } from "react";
import { sortReportsByCreatedAtDesc } from "../../utils/sortReports";

const ReportsList = ({ onActiveReportChange, onCreateNewReport, isDarkMode, reportsData, reportTypes = [], onEditModeChange, activeReport: parentActiveReport, showAddIcon = true, onAddClick, isReportTypesLoading }: any) => {
    const theme = useTheme();
    const [isLoading] = useState(false);
    const [activeReport, setActiveReport] = useState<any>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleReportClick = (report: any) => {
        // Only update if the report is different to prevent unnecessary refreshes
        if (activeReport?.id !== report.id) {
            setActiveReport(report);
            onActiveReportChange(report);
        }
        // Enable edit mode when clicking on a report
        onEditModeChange?.(false);
    };

    const handleAddClick = (event: React.MouseEvent<HTMLElement>) => {
        if (onAddClick) {
            onAddClick();
        }
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleReportTypeSelect = (reportType: any) => {
        onCreateNewReport(reportType);
        handleClose();
    };


    useEffect(() => {
        if (reportsData.length === 0) {
            setActiveReport(null);
            onActiveReportChange(null);
        } else if (
            activeReport &&
            !reportsData.some((report: any) => report.id === activeReport.id)
        ) {
            // Clear the active report if it's no longer in the filtered results
            setActiveReport(null);
            onActiveReportChange(null);
        }
        // Removed automatic selection of first report - no report selected by default
    }, [searchQuery, reportsData]);

    // Sync with parent's active report
    useEffect(() => {
        if (parentActiveReport && parentActiveReport.id !== activeReport?.id) {
            setActiveReport(parentActiveReport);
        } else if (!parentActiveReport && activeReport) {
            // Clear local active report when parent clears it
            setActiveReport(null);
        }
    }, [parentActiveReport]);


    // Updated to use correct property names from reportsData and sort by created_at descending
    const filteredReports = sortReportsByCreatedAtDesc(
        reportsData.filter(
            (report: any) =>
                report?.custom_report_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                report?.report?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                report?.status_display?.toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const getScheduleStyles = (isScheduled: boolean) => {
        if (isScheduled) {
            return {
                label: 'Scheduled',
                sx: {
                    backgroundColor: isDarkMode ? 'transparent' : '#E8F5E8', // light green
                    color: isDarkMode ? '#ffffff' : '#16A34A', // white text in dark mode, green text in light mode
                    border: '1px solid #16A34A',
                }
            };
        }
        return {
            label: 'Manual',
            sx: {
                backgroundColor: isDarkMode ? 'transparent' : '#F5F3FF', // very light purple
                color: isDarkMode ? '#ffffff' : '#3A2ADA', // white text in dark mode, purple text in light mode
                border: '1px solid #3A2ADA',
            }
        };
    };

    return (
        <>
            <Card className="white-box">
                <div
                    style={{
                        borderBottom: "1px solid #E2E2F2",
                    }}
                >
                    <form autoComplete="off">
                        <TextField
                            size="small"
                            variant="standard"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search Reports"
                            fullWidth
                            autoComplete="off"
                            name="disable-autofill-search"
                            sx={{
                                m: 1,
                                width: 'calc(100% - 16px)',
                                color: theme.palette.text.primary,
                                borderRadius: "6px",
                                border: "1px solid",
                                borderColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
                                backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    borderColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.24)" : "rgba(0, 0, 0, 0.24)",
                                },
                                "&:focus-within": {
                                    borderColor: isDarkMode ? theme.palette.primary.main : "",
                                    boxShadow: isDarkMode ? `0 0 0 2px ${theme.palette.primary.main}25` : "",
                                    backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
                                },
                                "& .MuiInputBase-root": {
                                    height: "32px !important",
                                    minHeight: "32px !important",
                                    alignItems: "center",
                                    paddingLeft: "8px",
                                    paddingRight: "8px",
                                },
                                "& .MuiInputBase-input": {
                                    color: theme.palette.text.primary,
                                    lineHeight: "32px",
                                    padding: "0px !important",
                                },
                                "& .MuiInputBase-input::placeholder": {
                                    color: theme.palette.text.secondary,
                                    opacity: 0.8,
                                },
                                "& fieldset": { border: "none" },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {searchQuery && (
                                            <IconButton size="small" onClick={() => setSearchQuery("")} sx={{ mr: 1 }}>
                                                <ClearIcon />
                                            </IconButton>
                                        )}
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            {showAddIcon && (
                                                <IconButton size="small" onClick={handleAddClick}>
                                                    <AddIcon sx={{ fill: isDarkMode ? "#ffffff" : "#3a2ada" }} />
                                                </IconButton>
                                            )}
                                            <Menu
                                                anchorEl={anchorEl}
                                                open={open}
                                                onClose={handleClose}
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                }}
                                                sx={{
                                                    '& .MuiPaper-root': {
                                                        minWidth: 200,
                                                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                                                        borderRadius: '8px',
                                                        border: '1px solid #E2E2F2',
                                                    }
                                                }}
                                                >
                                                {(() => {
                                                    if (isReportTypesLoading) {
                                                        return (
                                                            <MenuItem disabled sx={{ justifyContent: "center", py: 2 }}>
                                                                <CircularProgress size={20} />
                                                            </MenuItem>
                                                        );
                                                    }

                                                    // Normalize reportTypes to an array
                                                    const availableTypes = Array.isArray(reportTypes) ? reportTypes : [];

                                                    if (availableTypes.length === 0) {
                                                        return (
                                                            <MenuItem disabled>
                                                                <ListItemText>No report types available</ListItemText>
                                                            </MenuItem>
                                                        );
                                                    }

                                                    return availableTypes.map((reportType: any, index: number) => (
                                                        <MenuItem key={`report-type-${index}-${reportType.type || reportType.value}`} onClick={() =>
                                                            handleReportTypeSelect(reportType)}>
                                                            <ListItemText>{reportType.name || reportType.type}</ListItemText>
                                                        </MenuItem>
                                                    ));
                                                })()}
                                            </Menu>
                                        </Box>
                                    </InputAdornment>
                                ),
                                disableUnderline: true,
                            }}
                        />
                    </form>
                    <TableContainer
                        style={{
                            height: "calc(100vh - 150px)",
                            width: "100%",
                            overflow: "auto",
                            position: "relative",
                        }}
                        className="scroll"
                    >
                        {!isLoading && filteredReports.length > 0 ? (
                            <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
                                {filteredReports.map((report: any, index: number) => (
                                    <li
                                        key={`report-${report.id || index}-${report.custom_report_name}`}
                                        onClick={() => handleReportClick(report)}
                                        style={{
                                            cursor: "pointer",
                                            padding: "12px 16px",
                                            margin: "4px 8px",
                                            borderRadius: "8px",
                                            backgroundColor: activeReport?.id === report.id
                                                ? "#3A2ADA" // Solid blue background for selected report
                                                : index % 2 === 0
                                                    ? isDarkMode
                                                        ? "#333333"
                                                        : "#F8F9FA"
                                                    : isDarkMode
                                                        ? "#000000"
                                                        : "transparent",
                                            border: activeReport?.id === report.id
                                                ? "2px solid #3A2ADA" // Blue border for selected report
                                                : "1px solid transparent",
                                            transition: "all 0.3s ease",
                                            transform: activeReport?.id === report.id ? "scale(1.02)" : "scale(1)",
                                            boxShadow: activeReport?.id === report.id
                                                ? "0 4px 12px rgba(58, 42, 218, 0.3)" // Blue shadow for selected report
                                                : "none",
                                        }}
                                    >
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {/* Report Name + Status */}
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start'
                                            }}>
                                                <Tooltip title={report.custom_report_name} arrow placement="top">
                                                    <div style={{
                                                        fontWeight: 'bold',
                                                        fontSize: '14px',
                                                        color: activeReport?.id === report.id
                                                            ? "#FFFFFF" // White text for selected report
                                                            : (isDarkMode ? "#ffffff" : "#000000"), // Default colors
                                                        lineHeight: '1.2',
                                                        cursor: 'pointer',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {report.custom_report_name}
                                                    </div>
                                                </Tooltip>
                                                {(() => {
                                                    const schedule = getScheduleStyles(!!report.is_scheduled);
                                                    return (
                                                        <Chip
                                                            label={schedule.label}
                                                            size="small"
                                                            sx={{
                                                                ...schedule.sx,
                                                                fontSize: '11px',
                                                                fontWeight: 'semibold',
                                                                height: '24px',
                                                                borderRadius: '8px'
                                                            }}
                                                        />
                                                    );
                                                })()}
                                            </div>

                                            {/* Report Type */}
                                            <div style={{
                                                fontSize: '12px',
                                                opacity: activeReport?.id === report.id ? 0.9 : 0.7,
                                                color: activeReport?.id === report.id
                                                    ? "#E5E7EB" // Light gray for selected report
                                                    : (isDarkMode ? "#cccccc" : "#666666") // Default colors
                                            }}>
                                                {report.report}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 190px)' }}>
                                <p style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>
                                    {isLoading ? "Loading..." : "No data available to show"}
                                </p>
                            </div>
                        )}
                    </TableContainer>
                </div>
            </Card>
        </>
    );
};

export default ReportsList;
